import Hls from 'hls.js';

/**
 * Shared HLS Configuration - Standard (VOD/Recorded Content)
 * Used for channels and non-live content
 */
export const HLS_CONFIG = {
  enableWorker: true,
  lowLatencyMode: true,
  backBufferLength: 90,
  maxBufferLength: 30,
  maxMaxBufferLength: 600,
  maxBufferSize: 60 * 1000 * 1000, // 60 MB
  maxBufferHole: 0.5,
  highBufferWatchdogPeriod: 2,
  nudgeOffset: 0.1,
  nudgeMaxRetry: 3,
  maxFragLookUpTolerance: 0.25,
  liveSyncDurationCount: 3,
  liveMaxLatencyDurationCount: Infinity,
  liveDurationInfinity: true,
  enableWebVTT: false,
  enableCEA708Captions: false,
  stretchShortVideoTrack: false,
  maxAudioFramesDrift: 1,
  forceKeyFrameOnDiscontinuity: true,
  abrEwmaFastLive: 3,
  abrEwmaSlowLive: 9,
  abrEwmaFastVoD: 3,
  abrEwmaSlowVoD: 9,
  abrEwmaDefaultEstimate: 5000000,
  abrBandWidthFactor: 0.95,
  abrBandWidthUpFactor: 0.7,
  abrMaxWithRealBitrate: false,
  maxStarvationDelay: 4,
  maxLoadingDelay: 4,
  minAutoBitrate: 0,
  emeEnabled: false,
  debug: false,
  // 2025 Enhanced configuration
  manifestLoadingTimeOut: 15000,
  manifestLoadingMaxRetry: 3,
  manifestLoadingRetryDelay: 1000,
  fragLoadingTimeOut: 10000,
  fragLoadingMaxRetry: 2,
  fragLoadingRetryDelay: 1000,
  levelLoadingTimeOut: 8000,
  levelLoadingMaxRetry: 2,
  levelLoadingRetryDelay: 1000,
  enableStreamRecovery: true,
  enableWorkerPreventExceptions: true
};

/**
 * Live Streaming HLS Configuration - Optimized for Live Match Streams
 * Lower buffer sizes for reduced latency
 */
export const HLS_LIVE_CONFIG = {
  enableWorker: true,
  lowLatencyMode: true,
  maxBufferLength: 12,            // Small buffer for live streaming (3 segments * 4s)
  maxBufferSize: 30 * 1000 * 1000, // Reduced buffer size for live
  maxBufferHole: 0.3,             // Small buffer holes only
  autoStartLoad: true,
  startLevel: -1,                 // Auto-select quality
  debug: false,
  liveSyncDurationCount: 2,       // Stay very close to live edge (2 segments behind)
  liveMaxLatencyDurationCount: 4, // Maximum 4 segments behind live
  manifestLoadingTimeOut: 30000,
  manifestLoadingRetryCount: 6,
  manifestLoadingMaxRetry: 3,
  segmentLoadingTimeOut: 20000,
  fragLoadingRetryDelay: 2000,
  levelLoadingRetryDelay: 2000,
  fragLoadingMaxRetry: 15,
  levelLoadingMaxRetry: 8,
  maxLoadingDelay: 4,
  maxMaxBufferLength: 1200,       // Max buffer length (20 minutes)
  startFragPrefetch: true,
  testBandwidth: false,
  abrMaxWithRealBitrate: true,
  backBufferLength: 90,
  capLevelToPlayerSize: true,
  nudgeOffset: 0.1,
  nudgeMaxRetry: 3,
  maxSeekHole: 2,
  progressive: true,
  liveDurationInfinity: true,
  liveBackBufferLength: 30
};

/**
 * Initialize HLS player with error handling
 * @param {HTMLVideoElement} videoElement - Video DOM element
 * @param {string} streamUrl - Stream URL to load
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Maximum retry attempts (default: 3)
 * @param {Function} options.onManifestParsed - Callback when manifest is parsed
 * @param {Function} options.onError - Callback for fatal errors
 * @param {boolean} options.autoPlay - Auto-play video (default: true)
 * @param {boolean} options.isLive - Use live streaming config (default: false)
 * @param {Object} options.hlsConfig - Additional HLS configuration overrides
 * @param {Function} options.onQualitySwitch - Callback when quality is switched
 * @returns {Hls|null} HLS instance or null
 */
export function initializeHLS(videoElement, streamUrl, options = {}) {
  const {
    maxRetries = 3,
    onManifestParsed = null,
    onError = null,
    autoPlay = true,
    isLive = false,
    hlsConfig = {},
    onQualitySwitch = null
  } = options;

  if (!Hls.isSupported()) {
    // Try native HLS support (Safari)
    if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      console.log('📱 Using native HLS support');
      videoElement.src = streamUrl;
      if (autoPlay) {
        videoElement.play().catch(e => console.log('Autoplay blocked:', e));
      }
      return null;
    }
    console.error('HLS not supported in this browser');
    return null;
  }

  // Choose base configuration: live or standard
  const baseConfig = isLive ? HLS_LIVE_CONFIG : HLS_CONFIG;
  const config = { ...baseConfig, ...hlsConfig };
  const hls = new Hls(config);

  let errorRetryCount = 0;

  // Attach media and load source
  hls.attachMedia(videoElement);

  hls.on(Hls.Events.MEDIA_ATTACHED, () => {
    console.log('📺 Media attached, loading source...');
    hls.loadSource(streamUrl);
  });

  // Manifest parsed - ready to play
  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    console.log('✅ HLS manifest parsed');
    if (onManifestParsed) {
      onManifestParsed(hls);
    }
    if (autoPlay) {
      videoElement.play().catch(e => console.log('Autoplay blocked:', e));
    }
  });

  // Manifest loading
  hls.on(Hls.Events.MANIFEST_LOADING, () => {
    console.log('⏳ Loading HLS manifest...');
  });

  // Quality level switch event
  if (onQualitySwitch) {
    hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
      onQualitySwitch(data.level, hls.levels);
    });
  }

  // Error handling with retry logic
  hls.on(Hls.Events.ERROR, (event, data) => {
    console.error(`❌ HLS error (${data.type}):`, data.details);

    if (data.fatal) {
      errorRetryCount++;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          console.log(`🔄 Network error (attempt ${errorRetryCount}/${maxRetries})`);
          if (errorRetryCount <= maxRetries) {
            setTimeout(() => {
              try {
                hls.startLoad();
              } catch (e) {
                console.error('Failed to restart HLS:', e);
                tryNativeFallback(videoElement, streamUrl);
              }
            }, errorRetryCount * 2000);
          } else {
            console.error('Max network retries exceeded');
            tryNativeFallback(videoElement, streamUrl);
          }
          break;

        case Hls.ErrorTypes.MEDIA_ERROR:
          console.log(`🔄 Media error (attempt ${errorRetryCount}/${maxRetries})`);
          if (errorRetryCount <= maxRetries) {
            setTimeout(() => {
              try {
                hls.recoverMediaError();
              } catch (e) {
                console.error('Failed to recover media error:', e);
                tryNativeFallback(videoElement, streamUrl);
              }
            }, 1000);
          } else {
            console.error('Max media retries exceeded');
            hls.destroy();
          }
          break;

        case Hls.ErrorTypes.MUX_ERROR:
        case Hls.ErrorTypes.OTHER_ERROR:
        default:
          console.error('Fatal HLS error, cannot recover:', data.details);
          tryNativeFallback(videoElement, streamUrl);
          break;
      }

      if (onError) {
        onError(data);
      }
    } else {
      console.warn('⚠️ Non-fatal HLS error:', data.details);
    }
  });

  return hls;
}

/**
 * Try native HLS fallback
 */
function tryNativeFallback(videoElement, streamUrl) {
  if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
    console.log('🔄 Falling back to native HLS support');
    videoElement.src = streamUrl;
    videoElement.play().catch(e => console.error('Native playback failed:', e));
  }
}

/**
 * Clean up HLS instance
 * @param {Hls} hls - HLS instance to destroy
 */
export function destroyHLS(hls) {
  if (hls) {
    try {
      hls.destroy();
      console.log('🧹 HLS instance destroyed');
    } catch (e) {
      console.error('Error destroying HLS:', e);
    }
  }
}

/**
 * Get API base URL helper
 */
export function getApiBaseUrl() {
  return (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace('/api', '');
}
